import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ShowBoundarySummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

const SHOW_TYPE = 'MAIN_STAGE' as const;

function linesToList(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function listToLines(items: string[]): string {
  return items.join('\n');
}

export function ModelBoundariesCard(): ReactElement {
  const { api } = useAuth();
  const [hardNos, setHardNos] = useState('');
  const [negotiable, setNegotiable] = useState('');
  const [included, setIncluded] = useState('');
  const [otherBoundaries, setOtherBoundaries] = useState<ShowBoundarySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.getMyBoundaries();
    const boundary =
      result.boundaries.find((row) => row.showType === SHOW_TYPE) ??
      ({
        showType: SHOW_TYPE,
        hardNos: [],
        negotiable: [],
        included: [],
      } satisfies ShowBoundarySummary);
    setOtherBoundaries(result.boundaries.filter((row) => row.showType !== SHOW_TYPE));
    setHardNos(listToLines(boundary.hardNos));
    setNegotiable(listToLines(boundary.negotiable));
    setIncluded(listToLines(boundary.included));
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.boundaries.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateMyBoundaries({
        boundaries: [
          ...otherBoundaries,
          {
            showType: SHOW_TYPE,
            hardNos: linesToList(hardNos),
            negotiable: linesToList(negotiable),
            included: linesToList(included),
          },
        ],
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.boundaries.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.boundaries.loading')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.boundaries.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.boundaries.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}
      {saved ? (
        <p style={{ margin: '0 0 0.75rem', color: 'var(--fm-color-secondary)', fontSize: '0.875rem' }}>
          {t('common.studio.boundaries.saved')}
        </p>
      ) : null}

      <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: 'var(--fm-color-text-muted)' }}>
        {t('common.studio.boundaries.showType')}: {t('common.studio.boundaries.mainStage')}
      </p>

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <label>
          {t('common.studio.boundaries.hardNos')}
          <textarea
            className="fm-input"
            value={hardNos}
            disabled={isSaving}
            rows={4}
            placeholder={t('common.studio.boundaries.lineHint')}
            onChange={(event) => {
              setHardNos(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem', resize: 'vertical' }}
          />
        </label>
        <label>
          {t('common.studio.boundaries.negotiable')}
          <textarea
            className="fm-input"
            value={negotiable}
            disabled={isSaving}
            rows={4}
            placeholder={t('common.studio.boundaries.lineHint')}
            onChange={(event) => {
              setNegotiable(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem', resize: 'vertical' }}
          />
        </label>
        <label>
          {t('common.studio.boundaries.included')}
          <textarea
            className="fm-input"
            value={included}
            disabled={isSaving}
            rows={4}
            placeholder={t('common.studio.boundaries.lineHint')}
            onChange={(event) => {
              setIncluded(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem', resize: 'vertical' }}
          />
        </label>
        <Button
          label={isSaving ? t('common.studio.boundaries.saving') : t('common.studio.boundaries.save')}
          disabled={isSaving}
          onClick={() => {
            void handleSave();
          }}
        />
      </div>
    </div>
  );
}
